import { cn } from "@/lib/cn";
import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";

interface ApprovalCardProps {
  action: string;
  description: string;
  riskLevel: "low" | "medium" | "high";
  onApprove?: () => void;
  onEdit?: () => void;
  onReject?: () => void;
  className?: string;
}

const RISK_CONFIG = {
  low: {
    label: "Low risk",
    style: {
      backgroundColor: "var(--color-success-muted)",
      color: "var(--color-success)",
      borderColor: "var(--color-success)",
    },
  },
  medium: {
    label: "Medium risk",
    style: {
      backgroundColor: "var(--color-warning-muted)",
      color: "var(--color-warning)",
      borderColor: "var(--color-warning)",
    },
  },
  high: {
    label: "High risk",
    style: {
      backgroundColor: "var(--color-danger-muted)",
      color: "var(--color-danger)",
      borderColor: "var(--color-danger)",
    },
  },
};

export function ApprovalCard({
  action,
  description,
  riskLevel,
  onApprove,
  onEdit,
  onReject,
  className,
}: ApprovalCardProps) {
  const risk = RISK_CONFIG[riskLevel];

  return (
    <div
      className={cn(
        "w-80 rounded-xl border border-glass-stroke bg-glass-fill backdrop-blur-md shadow-lg p-4 flex flex-col gap-3",
        className,
      )}
      data-testid="approval-card"
    >
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="mt-0.5 rounded-lg bg-yellow-500/10 p-1.5">
          <Icons.AlertTriangle className="h-4 w-4 text-yellow-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-canvas-base font-semibold text-muted uppercase tracking-wide">
            Approval required
          </p>
          <p className="text-canvas-md font-semibold text-primary mt-0.5">{action}</p>
        </div>
      </div>

      {/* Description */}
      <p className="text-canvas-base text-secondary leading-relaxed">{description}</p>

      {/* Risk badge */}
      <div
        className="inline-flex items-center gap-1.5 self-start rounded-md border px-2 py-0.5 text-canvas-base font-semibold"
        style={risk.style}
        data-testid="approval-card-risk-badge"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {risk.label}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <Button
          className={cn(
            "flex-1 text-black hover:opacity-90 hover:brightness-100",
          )}
          style={{ backgroundColor: "var(--color-accent)" }}
          onClick={onApprove}
          data-testid="approval-card-approve-btn"
        >
          Approve
        </Button>
        <Button
          variant="secondary"
          onClick={onEdit}
          data-testid="approval-card-edit-btn"
        >
          Edit
        </Button>
        <Button
          variant="destructive"
          onClick={onReject}
          data-testid="approval-card-reject-btn"
        >
          Reject
        </Button>
      </div>
    </div>
  );
}
